import React, { useEffect, useState, useCallback } from "react";
import './css/Teachers.css';
import { MagnifyingGlassIcon, AdjustmentsHorizontalIcon, UsersIcon, ArrowPathIcon, XMarkIcon, AcademicCapIcon } from "@heroicons/react/24/outline";
import { StarIcon,ExclamationCircleIcon } from "@heroicons/react/24/solid";
import TeacherCard from "../components/common/TeacherCard";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive"; // Add this if you use react-responsive
import { fetchWithToken } from '../services/api';

const FilterGroup = ({ title, children }) => (
  <div className="filter-group">
    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{title}</h4>
    {children}
  </div>
);

const CategoryGroup = ({ category, selectedRootCategory, subCategories, selectedCategories, onRootSelect, onSubSelect }) => (
  <div className="mb-3">
    <button
      onClick={() => onRootSelect(category.id)}
      className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all ${
        selectedRootCategory === category.id
          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
    >
      <span className="font-medium">{category.name}</span>
      <svg
        className={`w-5 h-5 transition-transform ${selectedRootCategory === category.id ? 'rotate-180' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    
    {/* Sub Categories Collapse */}
    {selectedRootCategory === category.id && subCategories.length > 0 && (
      <div className="mt-2 ml-4 space-y-1 border-l-2 border-gray-100 dark:border-gray-700">
        {subCategories.map(sub => (
          <label
            key={sub.id}
            className="flex items-center p-2 pl-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-r-lg cursor-pointer group"
          >
            <input
              type="checkbox"
              className="filter-checkbox"
              checked={selectedCategories.includes(sub.id)}
              onChange={() => onSubSelect(sub.id)}
            />
            <span className="ml-3 text-sm text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-300">
              {sub.name}
            </span>
          </label>
        ))}
      </div>
    )}
  </div>
);

const TeacherListPage = () => {
  // State Management
  const [state, setState] = useState({
    teachers: [],
    categories: [],
    subCategories: [],
    totalPages: 1,
    error: null
  });

  const [filters, setFilters] = useState({
    searchTerm: "",
    selectedRootCategory: null,
    selectedCategories: [],
    sortByRating: false,
    page: 0
  });

  const [ui, setUi] = useState({
    loading: true,
    filterOpen: false,
    categoryLoading: false
  });

  // Mobile filter drawer state
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

  // Memoized fetch function
  const fetchTeachers = useCallback(async () => {
    setUi(prev => ({ ...prev, loading: true }));
    
    try {
      // Filtre varsa POST isteği yap, yoksa normal GET isteği yap
      if (filters.searchTerm || filters.selectedCategories.length > 0 || filters.sortByRating) {
        const filterPayload = {
          keyword: filters.searchTerm || null,
          categoryIds: filters.selectedCategories.length > 0 ? filters.selectedCategories : null,
          sortByRating: filters.sortByRating || false
        };

        const response = await fetch(`/v1/teacher/filter?page=${filters.page}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(filterPayload)
        });

        const data = await response.json();
        
        if (!data.success) throw new Error(data.message || 'Veri alınamadı');

        setState(prev => ({
          ...prev,
          teachers: data.data?.content || [],
          totalPages: data.data?.totalPages || 1,
          error: null
        }));
      } else {
        // Filtre yoksa normal GET isteği
        const response = await fetch(`/v1/teacher/all?page=${filters.page}`);
        const data = await response.json();

        // Backend Page<TeacherResponse> döndürüyor: data.data.content ve data.data.totalPages
        if (!data.success) throw new Error(data.message || 'Veri alınamadı');

        setState(prev => ({
          ...prev,
          teachers: data.data?.content || [],
          totalPages: data.data?.totalPages || 1,
          error: null
        }));
      }
    } catch (error) {
      setState(prev => ({ ...prev, error: error.message }));
    } finally {
      setUi(prev => ({ ...prev, loading: false }));
    }
  }, [filters]);

  // Root categories fetch
  const fetchRootCategories = useCallback(async () => {
    try {
      const [rootResponse, subResponse] = await Promise.all([
        fetch('/v1/categories/root'),
        filters.selectedRootCategory 
          ? fetch(`/v1/categories/${filters.selectedRootCategory}/subcategories`)
          : Promise.resolve(null)
      ]);

      const rootData = await rootResponse.json();
      const subData = subResponse ? await subResponse.json() : null;

      setState(prev => ({
        ...prev,
        categories: rootData.success ? rootData.data : [],
        subCategories: subData?.success ? subData.data : []
      }));
    } catch (error) {
      console.error("Categories fetch error:", error);
    }
  }, [filters.selectedRootCategory]);

  // Effects
  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useEffect(() => {
    fetchRootCategories();
  }, [fetchRootCategories]);

  // Handlers
  const handleFilterChange = (changes) => {
    setFilters(prev => ({
      ...prev,
      ...changes,
      page: typeof changes.page === "number" ? changes.page : 0
    }));
  };

  const resetFilters = () => {
    setFilters({
      searchTerm: "",
      selectedRootCategory: null,
      selectedCategories: [],
      sortByRating: false,
      page: 0
    });
  };

  const toggleCategorySelection = (categoryId) => {
    setFilters(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.includes(categoryId)
        ? prev.selectedCategories.filter(id => id !== categoryId)
        : [...prev.selectedCategories, categoryId]
    }));
  };

  // Compute active filters count
  const activeFiltersCount = [
    filters.searchTerm,
    ...filters.selectedCategories,
    filters.sortByRating
  ].filter(Boolean).length;

  // Animasyon varyantları
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  // --- New: Render categories as accordion, subcategories directly under selected root ---
  const renderFilterSidebar = (isMobileDrawer = false) => (
    <aside
      className={`filter-sidebar${isMobileDrawer ? " mobile-filter-drawer" : ""} ${
        isMobileDrawer && mobileFilterOpen ? " open" : ""
      }`}
      style={
        isMobileDrawer
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 50,
              width: "100vw",
              height: "100vh",
              background: "rgba(255,255,255,0.97)",
              transition: "transform 0.3s",
              transform: mobileFilterOpen ? "translateX(0)" : "translateX(-100%)",
              overflowY: "auto",
              padding: "1.5rem",
            }
          : {}
      }
    >
      {/* Mobile close button */}
      {isMobileDrawer && (
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Filtreler
          </h3>
          <button
            onClick={() => setMobileFilterOpen(false)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Filtreyi Kapat"
          >
            <XMarkIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
          </button>
        </div>
      )}
      {/* ...existing filter sidebar content (sorting, categories, active filters)... */}
      {/* ...copy the rest of the sidebar code here, unchanged... */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          Filtreler
        </h3>
        {activeFiltersCount > 0 && (
          <button
            onClick={resetFilters}
            className="text-sm px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            Temizle
          </button>
        )}
      </div>
      {/* ...rest of sidebar content... */}
      {/* Sıralama */}
      <div className="mb-6 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <label className="flex items-center cursor-pointer group">
          <input
            type="checkbox"
            className="filter-checkbox"
            checked={filters.sortByRating}
            onChange={() => handleFilterChange({ sortByRating: !filters.sortByRating })}
          />
          <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600">
            <StarIcon className="h-4 w-4 text-yellow-400 inline mr-1" />
            En yüksek puanlılar
          </span>
        </label>
      </div>
      {/* Kategoriler Accordion */}
      <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-300 mb-3">Kategoriler</h4>
        <div className="space-y-1">
          {state.categories.map(category => (
            <div key={category.id} className="mb-1">
              <button
                onClick={() => handleFilterChange({
                  selectedRootCategory: filters.selectedRootCategory === category.id ? null : category.id,
                  selectedCategories: []
                })}
                className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all ${
                  filters.selectedRootCategory === category.id
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 font-semibold'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <span>{category.name}</span>
                <svg
                  className={`w-5 h-5 transition-transform ${filters.selectedRootCategory === category.id ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {filters.selectedRootCategory === category.id && state.subCategories.length > 0 && (
                <div className="mt-2 ml-2 pl-2 border-l-2 border-blue-100 dark:border-blue-800 space-y-1">
                  {state.subCategories.map(sub => (
                    <label
                      key={sub.id}
                      className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        className="filter-checkbox"
                        checked={filters.selectedCategories.includes(sub.id)}
                        onChange={() => toggleCategorySelection(sub.id)}
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-300">
                        {sub.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Aktif Filtreler */}
      {activeFiltersCount > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Aktif Filtreler
          </div>
          <div className="flex flex-wrap gap-2">
            {/* ...existing active filter chips... */}
          </div>
        </div>
      )}
    </aside>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section with Search */}
      <motion.div 
        className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 dark:from-blue-900 dark:via-indigo-900 dark:to-purple-900 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Background pattern and details */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 opacity-20 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-gradient-to-tr from-purple-400 to-indigo-400 opacity-20 blur-3xl"></div>
          <div className="absolute inset-0 opacity-10 bg-grid-pattern"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-12 relative z-10">
          <motion.h1 
            className="text-3xl md:text-4xl font-bold text-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Uzman Eğitmenlerle Öğrenin
            </span>
          </motion.h1>
          
          {/* Enhanced Search Input */}
          <motion.div 
            className="max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="courses-search relative group">
              <input
                type="text"
                placeholder="İsim, alan veya konum ile arayın..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange({ searchTerm: e.target.value })}
                className="courses-search-input w-full px-6 py-4 pl-14 rounded-2xl border-2 focus:ring-4 focus:ring-blue-400/30 shadow-lg text-gray-800 text-base transition-all duration-200"
              />
              <MagnifyingGlassIcon className="courses-search-icon absolute left-5 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
              {filters.searchTerm && (
                <button
                  onClick={() => handleFilterChange({ searchTerm: "" })}
                  className="courses-search-clear absolute right-5 top-1/2 transform -translate-y-1/2 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Content with Sidebar Layout */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="teachers-layout">
          {/* Mobile filter toggle button */}
          <div className="block lg:hidden mb-4">
            <button
              onClick={() => setMobileFilterOpen(true)}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium shadow hover:bg-blue-700 transition"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5 inline mr-2" />
              Filtreleri Aç
            </button>
          </div>
          {/* Sidebar: show on desktop, or as drawer on mobile */}
          <div className="hidden lg:block">{renderFilterSidebar(false)}</div>
          {isMobile && renderFilterSidebar(true)}
          <main>
            {/* Results Count */}
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {state.teachers.length} Eğitmen Bulundu
              </h2>
            </div>

            {/* Results Grid - using existing AnimatePresence and motion.div */}
            <AnimatePresence mode="wait">
              {ui.loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  {[...Array(6)].map((_, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden">
                      <div className="p-6 flex flex-col items-center">
                        <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-full mb-5 relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700 animate-pulse"></div>
                        </div>
                        <div className="w-3/4 h-5 bg-gray-200 dark:bg-gray-700 rounded-full mb-3 animate-pulse"></div>
                        <div className="w-1/3 h-4 bg-gray-200 dark:bg-gray-700 rounded-full mb-5 animate-pulse"></div>
                        <div className="w-1/2 h-8 bg-gray-200 dark:bg-gray-700 rounded-full mb-5 animate-pulse"></div>
                        <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded-full mb-3 animate-pulse"></div>
                        <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded-full mb-5 animate-pulse"></div>
                      </div>
                      <div className="h-12 bg-gray-100 dark:bg-gray-700 animate-pulse"></div>
                    </div>
                  ))}
                </motion.div>
              ) : state.error ? (
                <motion.div
                  key="error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-8 text-center"
                >
                  <div className="text-red-500 mb-4">
                    <ExclamationCircleIcon className="h-12 w-12 mx-auto" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Bir Hata Oluştu
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{state.error}</p>
                  <button
                    onClick={fetchTeachers}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Tekrar Dene
                  </button>
                </motion.div>
              ) : state.teachers.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-10 text-center"
                >
                  <div className="w-24 h-24 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-6">
                    <AcademicCapIcon className="h-12 w-12 text-blue-500 dark:text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Eğitmen Bulunamadı</h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
                    {activeFiltersCount > 0 
                      ? "Arama kriterlerinize uygun eğitmen bulunamadı. Lütfen farklı filtreler deneyin."
                      : "Şu anda görüntülenecek eğitmen bulunmuyor."
                    }
                  </p>
                  {activeFiltersCount > 0 && (
                    <button 
                      onClick={resetFilters}
                      className="py-2.5 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
                    >
                      Filtreleri Temizle
                    </button>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="results"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="space-y-8"
                >
                  {/* Results Header */}
                  {filters.searchTerm && (
                    <div className="text-gray-600 dark:text-gray-400">
                      <span>"{filters.searchTerm}" için {state.teachers.length} eğitmen bulundu</span>
                    </div>
                  )}

                  {/* Teacher Cards Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {state.teachers.map((teacher) => (
                      <motion.div
                        key={teacher.id}
                        variants={itemVariants}
                        whileHover={{ y: -8 }}
                        className="h-full"
                      >
                        <TeacherCard teacher={teacher} />
                      </motion.div>
                    ))}
                  </div>

                  {/* Enhanced Pagination */}
                  {state.totalPages > 1 && (
                    <div className="mt-12 flex flex-col items-center">
                      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden p-1.5 flex flex-wrap justify-center">
                        <button
                          onClick={() => handleFilterChange({ page: 0 })}
                          disabled={filters.page === 0}
                          className="px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:hover:bg-transparent"
                          aria-label="İlk sayfa"
                        >
                          ⏮️ İlk
                        </button>
                        <button
                          onClick={() => handleFilterChange({ page: filters.page - 1 })}
                          disabled={filters.page === 0}
                          className="px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:hover:bg-transparent"
                          aria-label="Önceki sayfa"
                        >
                          ◀️ Önceki
                        </button>
                        <div className="flex px-1">
                          {/* Modern Pagination: Sadece yakın sayfalar ve ... */}
                          {(() => {
                            const pages = [];
                            const total = state.totalPages;
                            const current = filters.page;
                            if (total <= 7) {
                              for (let i = 0; i < total; i++) {
                                pages.push(
                                  <button
                                    key={i}
                                    onClick={() => handleFilterChange({ page: i })}
                                    className={`w-10 h-10 rounded-lg ${
                                      current === i
                                        ? "bg-blue-600 text-white font-medium"
                                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                    }`}
                                  >
                                    {i + 1}
                                  </button>
                                );
                              }
                            } else {
                              // Her zaman ilk sayfa
                              pages.push(
                                <button
                                  key={0}
                                  onClick={() => handleFilterChange({ page: 0 })}
                                  className={`w-10 h-10 rounded-lg ${
                                    current === 0
                                      ? "bg-blue-600 text-white font-medium"
                                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                  }`}
                                >
                                  1
                                </button>
                              );
                              // ... gerekiyorsa
                              if (current > 3) {
                                pages.push(<span key="start-ellipsis" className="w-10 h-10 flex items-center justify-center">...</span>);
                              }
                              // Ortadaki sayfalar
                              for (let i = Math.max(1, current - 2); i <= Math.min(total - 2, current + 2); i++) {
                                if (i === 0 || i === total - 1) continue;
                                pages.push(
                                  <button
                                    key={i}
                                    onClick={() => handleFilterChange({ page: i })}
                                    className={`w-10 h-10 rounded-lg ${
                                      current === i
                                        ? "bg-blue-600 text-white font-medium"
                                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                    }`}
                                  >
                                    {i + 1}
                                  </button>
                                );
                              }
                              // ... gerekiyorsa
                              if (current < total - 4) {
                                pages.push(<span key="end-ellipsis" className="w-10 h-10 flex items-center justify-center">...</span>);
                              }
                              // Son sayfa
                              pages.push(
                                <button
                                  key={total - 1}
                                  onClick={() => handleFilterChange({ page: total - 1 })}
                                  className={`w-10 h-10 rounded-lg ${
                                    current === total - 1
                                      ? "bg-blue-600 text-white font-medium"
                                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                  }`}
                                >
                                  {total}
                                </button>
                              );
                            }
                            return pages;
                          })()}
                        </div>
                        <button
                          onClick={() => handleFilterChange({ page: filters.page + 1 })}
                          disabled={filters.page >= state.totalPages - 1}
                          className="px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:hover:bg-transparent"
                          aria-label="Sonraki sayfa"
                        >
                          Sonraki ▶️
                        </button>
                        <button
                          onClick={() => handleFilterChange({ page: state.totalPages - 1 })}
                          disabled={filters.page >= state.totalPages - 1}
                          className="px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:hover:bg-transparent"
                          aria-label="Son sayfa"
                        >
                          Son ⏭️
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                        Sayfa {filters.page + 1} / {state.totalPages || 1}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
};

export default TeacherListPage;
